import React, { useCallback, useState, useEffect, useMemo } from 'react';

import { useMediaQuery, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'contexts/auth';
import api from 'services/api';
import Centered from 'components/Centered';
import { toast } from 'react-toastify';
import { encryptData, decryptData } from 'utils/crypto';
import ItauCredResumoBoleto from './ItauCredResumoBoleto';
import GenericResumoBoleto from './GenericResumoBoleto';
import BradescoResumoBoleto2 from './BradescoResumoBoleto2';
import { IDivida } from 'interfaces/Divida';
import {
  IOpcaoPagamento,
  IBuscaOpcaoPagamento,
} from 'interfaces/BuscaOpcaoPagamento';
import { IResumoBoleto } from 'interfaces/ResumoBoleto';
import { ICustomizacao } from 'interfaces/Customizacao';
import { buscaCustomService } from 'services/buscaCustomizacao/buscaCustomizacao';
import ContatoDialog from 'dialogs/ContatoDialog';
import { AcaoEvento, registrarEventoAsync } from 'services/TrackingService';
import { useTracking } from 'contexts/tracking';
import ModalCsat from 'components/Modal/ModalCsat';
import LoadingEmitirBoletoOverlay from 'components/LoadingBoleto/LoadingBoleto';


const ResumoBoleto: React.FC = () => {
  const [custom, setCustom] = useState<ICustomizacao>({});
  const { user, bank, dividasAuthContext } = useAuth();
  const [openContatoDialog, setOpenContatoDialog] = useState(false);
  const isMobile = useMediaQuery((th: any) => th?.breakpoints?.down('sm'));
  const navigate = useNavigate();
  const isDesktop = useMediaQuery((theme: any) => theme?.breakpoints?.up('sm'));
  const [resumoBoleto, setResumoBoleto] = useState<IResumoBoleto>(
    decryptData(localStorage.getItem('@resumoBoletoDivida') || '{}'),
  );
  const [divida, setDivida] = useState<IDivida>(
    decryptData(localStorage.getItem('@dividaSucesso') || '{}'),
  );
  const [opcao] = useState<IBuscaOpcaoPagamento>(
    decryptData(localStorage.getItem('@opcao') || '{}'),
  );
  const [parcelamento, setParcelamento] = useState<IOpcaoPagamento>(
    decryptData(localStorage.getItem('@resumoBoletoParcelamento') || '{}'),
  );
  const [dataValidade, setDataValidade] = useState(
    decryptData(localStorage.getItem('@dataValidade') || '{}'),
  );
  const [loading, setLoading] = useState(true);
  const [loadingEmitirBoleto, setLoadingEmitirBoleto] = useState(false);

  const [openCsat, setOpenCsat] = useState(false);
  const [acordoId, setAcordoId] = useState<string | null>(null);

  const { trackId } = useParams<{ trackId: string | undefined }>()
  const { sendTracking } = useTracking();

  const carteiraIds = useMemo(
    () => [
      divida?.carteiraId,
      divida?.carteiraId,
    ],
    [divida],
  );

  const carteiras = useMemo(
    () => [
      {
        crmId: divida?.crm,
        carteiraId: divida?.carteiraId,
        fase: divida?.fase,
        ranking: divida?.ranking,
      },
      {
        crmId: divida?.crm,
        carteiraId: divida?.carteiraId,
        fase: divida?.fase,
        ranking: divida?.ranking,
      },
    ],
    [divida],
  );

  const usaResumoApi: boolean = useMemo(() => {
    return !!opcao?.chamarResumoBoleto;
  }, [opcao]);

  const getResumoBoleto = useCallback(async () => {
    const parcelamento: IOpcaoPagamento = decryptData(
      localStorage.getItem('@resumoBoletoParcelamento') || '{}',
    );

    const updateDivida = dividasAuthContext?.find(
      _ => _.documento === divida?.documento,
    );
    const dataValidade = decryptData(
      localStorage.getItem('@dataValidade') || '{}',
    );

    if (!parcelamento || !divida) {
      navigate('/dashboard');
      return;
    }

    setResumoBoleto(
      decryptData(localStorage.getItem('@resumoBoletoDivida') || '{}'),
    );
    setDivida(updateDivida as IDivida);
    setParcelamento(parcelamento);
    setDataValidade(dataValidade);
    setLoading(false);
  }, [navigate, dividasAuthContext, divida]);

  const onEmitirBoleto = useCallback(async () => {
    setLoadingEmitirBoleto(true);

    sendTracking({
      trackId, acao: AcaoEvento.FormalizarAcordo,
      contratoId: divida?.numero,
      cpfCnpj: user?.cnpJ_CPF,
      crm: divida?.crm,
      carteiraId: divida?.carteiraId
    });

    const dataVencimento = !usaResumoApi
      ? dataValidade
      : resumoBoleto.dataVencimentoEntrada !== null
        ? resumoBoleto.dataVencimentoEntrada
        : resumoBoleto.dataVencimentoPrimeiraParcela;

    const identificador = !usaResumoApi
      ? parcelamento?.codigo
      : resumoBoleto.identificador;

    api.post('emitir-boleto', {
      Crm: divida?.crm,
      Contrato: divida?.numero,
      Carteira: divida?.carteiraId,
      CNPJ_CPF: user?.cnpJ_CPF,
      Valor: parcelamento?.valor,
      Parcelas: Number(parcelamento.parcelas?.length),
      DataVencimento: dataVencimento,
      Identificador: identificador,
      OpcaoDebitoConta: parcelamento.opcaoDebitoConta,
      OpcaoChequeEspecial: parcelamento.opcaoChequeEspecial,
      TipoContrato: divida?.tipoContrato
    })
      .then(({ data }) => {
        if (!!data?.sucesso) {

          const acordoIdStr = String(data.acordoId);

          setAcordoId(acordoIdStr);
          sessionStorage.setItem('@acordoIdCsat', encryptData(acordoIdStr));

          toast.success(data.msgRetorno);

          localStorage.setItem('@dividaSucesso', encryptData(divida));
          localStorage.setItem('@retornoEmitirBoleto', encryptData(data));
          localStorage.setItem('@resumoBoleto', encryptData(resumoBoleto));

          setOpenCsat(true);

          return;

        } else {

          toast.error(data.msgRetorno);
          setOpenContatoDialog(true);

          return;
        }
      })
      .catch((response) => {
        setOpenContatoDialog(true);
        return;
      })
      .finally(() => setLoadingEmitirBoleto(false));
  }, [
    usaResumoApi,
    resumoBoleto,
    divida,
    parcelamento,
    dataValidade,
    navigate,
    user,
  ]);

  useEffect(() => {
    sendTracking({
      trackId,
      acao: AcaoEvento.VisualizarDivida,
      contratoId: divida?.numero,
      cpfCnpj: user?.cnpJ_CPF,
      crm: divida?.crm,
      carteiraId: divida?.carteiraId
    });
    async function buscaCustomizacao() {
      if (divida?.crm && divida?.carteiraId) {
        const { data: newCustom } = await buscaCustomService({
          crms: divida?.crm,
          carteiraId: divida?.carteiraId,
        })
        setCustom(newCustom);
      }
    }
    buscaCustomizacao();
  }, []);

  useEffect(() => {
    getResumoBoleto();
  }, [getResumoBoleto]);

  if (loading) {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  const acordoIdStorage = sessionStorage.getItem('@acordoIdCsat');

  const acordoIdFinal =
    acordoId ?? (acordoIdStorage ? decryptData(acordoIdStorage) : "");

  return (
    <>
      <LoadingEmitirBoletoOverlay open={loadingEmitirBoleto} />
      
      <ContatoDialog
        onClose={() => setOpenContatoDialog(false)}
        open={openContatoDialog}
        carteiraIds={carteiraIds}
        carteiras={carteiras}
        initialCateriaId={divida?.carteiraId}
        customMessage={custom.ResumoBoletoContatoDialog}
      />
     <ModalCsat
        open={openCsat}
        onClose={() => {
          setOpenCsat(false);
          navigate('/dashboard/detalhes-divida');
        }}
        cpfCnpj={user?.cnpJ_CPF ?? ""}
        contrato={divida?.numero ?? ""}
        acordoId={acordoIdFinal}
        crmId={divida?.crm}
        carteiraId={divida?.carteiraId}
      />
      {usaResumoApi && bank?.financeira?.toLowerCase() === 'itaú' ? (
        <ItauCredResumoBoleto
          navigate={navigate}
          custom={custom}
          isDesktop={isDesktop}
          resumoBoleto={resumoBoleto}
          divida={divida}
          parcelamento={parcelamento}
          bank={bank}
          onEmitirBoleto={onEmitirBoleto}
          loadingEmitirBoleto={loadingEmitirBoleto}
        />
      ) : bank?.financeira?.toLowerCase() === 'bradesco' ? (
        <BradescoResumoBoleto2
          navigate={navigate}
          custom={custom}
          isDesktop={isDesktop}
          resumoBoleto={resumoBoleto}
          divida={divida}
          parcelamento={parcelamento}
          bank={bank}
          onEmitirBoleto={onEmitirBoleto}
          loadingEmitirBoleto={loadingEmitirBoleto}
          isMobile={isMobile}
          usaResumoApi={usaResumoApi}
        />
      ) : (
        <GenericResumoBoleto
          navigate={navigate}
          custom={custom}
          isDesktop={isDesktop}
          isMobile={isMobile}
          divida={divida}
          parcelamento={parcelamento}
          bank={bank}
          onEmitirBoleto={onEmitirBoleto}
          loadingEmitirBoleto={loadingEmitirBoleto}
        />
      )}
    </>
  )
};

export default ResumoBoleto;
